import type { GlobalConfig } from '@ad-lucy/context/adData';
import { StringUtils, TrackerUtils, type TrackingInformation, UriUtils } from '@ad-lucy/utils';
import {
  type Event,
  type GlobalDataQueryParams,
  type MouseEvent,
  mouseEvents,
} from '@ad-lucy/utils/tracking/types';
import { ApiConfig } from '../../../config/api';

export interface TrackingVariables {
  advertisementId: string;
  advertisementPanelId: string;
  reference: string | undefined;
  branchId: string | undefined;
}

export const replaceTrackingVariables = (
  trackingText: string,
  { advertisementId, advertisementPanelId, reference, branchId }: TrackingVariables,
): string =>
  trackingText
    .replace('%wmid%', advertisementId)
    .replace('{{wmid}}', advertisementId)
    .replace('%pid%', advertisementPanelId)
    .replace('{{pid}}', advertisementPanelId)
    .replace('%ref%', reference ?? '')
    .replace('{{ref}}', reference ?? '')
    .replace('%filialid%', branchId ?? '')
    .replace('{{filialid}}', branchId ?? '');

export const createTrackingElement = (
  trackingCodeTemplate: string,
  trackingVariables: TrackingVariables,
  windowLocation: string,
): void => {
  const trackingCode = replaceTrackingVariables(trackingCodeTemplate, trackingVariables);

  if (ApiConfig.environment === 'LIVE') {
    TrackerUtils.createTrackingWrapper(UriUtils.replaceVariables(trackingCode, windowLocation));
  }
};

/**
 * Checks whether the provided tracking event type is specifically a mouse event (has mouseX, mouseY, ad fraud detection).
 */
export const eventIsMouseEvent = (event: Event): event is MouseEvent => {
  // Copy the strict MouseEvent strings into a lax Event-type array to avoid type mismatches.
  const eventList: readonly Event[] = mouseEvents;
  return eventList.includes(event);
};

export const detectBotClick = (
  trackingInformation: Pick<TrackingInformation, 'event' | 'mouseX' | 'mouseY'>,
): boolean => {
  const { event, mouseX, mouseY } = trackingInformation;
  // Fraud-checking only applies to click events.
  if (event !== 'Click') {
    return false;
  }

  const hasValidMousePosition =
    mouseX !== null && mouseY !== null && mouseX !== undefined && mouseY !== undefined;

  // Since we know it's a mouse event, it shouldn't have an invalid mouse position.
  if (!hasValidMousePosition) {
    return true;
  }

  return (
    StringUtils.getLastCharacter(mouseX.toString()) === '0' &&
    StringUtils.getLastCharacter(mouseY.toString()) === '0' &&
    StringUtils.getLastCharacter(window.innerWidth.toString()) === '0' &&
    StringUtils.getLastCharacter(window.innerHeight.toString()) === '0'
  );
};

// function is used to read additional variables from the query parameter.
// This is used to add trackers with query parameter and now only over the AdCreator with the Tracker setting.
export const additionalQueryTrackers = (windowLocation: string, queryParam: string): void => {
  const ct = UriUtils.getQueryParamByName(windowLocation, queryParam);
  if (ct) {
    try {
      fetch(decodeURIComponent(ct), {
        method: 'GET',
      }).catch(() => []);
    } catch (err) {}
  }
};

export const parseURLToHost = (url: string): string | null => {
  try {
    const urlObj = new URL(decodeURIComponent(url));
    const { host } = urlObj;

    return host;
  } catch {
    return null;
  }
};

export const getHomepageParameter = (params: GlobalDataQueryParams): string | null => {
  return params.refdomain || parseURLToHost(params.ref);
};

// Helperfunction to create Tracking-Code-Templates
export const getTrackingCodeTemplate = (
  globalData: GlobalConfig,
  clickTagUrl: string | null,
  isUrlTrackingEnabled: boolean,
): string => {
  const disableExclusionPanel = globalData.meta.app_disableExclusionPixel;
  const defaultTrackingPixel = globalData.country?.defaultTrackingPixel ?? '';

  let clickTagImgTag: string | undefined;
  if (isUrlTrackingEnabled && clickTagUrl) {
    const separator = clickTagUrl.includes('?') ? '&' : '?';
    clickTagImgTag = `<img src="${clickTagUrl}${separator}sid2=%filialid%" width="1" height="1" border="0" />`;
  }

  // Use the clickTagUrl if URLTracking is enabled, otherwise use defaultTrackingPixel
  const baseTrackingPixel = clickTagImgTag ?? defaultTrackingPixel;
  // If disableExclusionPanel is true, remove clex-Param
  const cleanedTrackingUrl = disableExclusionPanel
    ? baseTrackingPixel.replace(/A?clex=[^&]+&*/g, '')
    : baseTrackingPixel;

  // Add uniqueTrackingPixel if available
  return globalData.uniqueTrackingPixel && cleanedTrackingUrl
    ? cleanedTrackingUrl + globalData.uniqueTrackingPixel
    : cleanedTrackingUrl;
};
